import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronRight,
  DollarSign,
  Eye,
  History,
  Loader2,
  Package2,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { formatDate, formatPrice } from "../../utils/format";
import { ProductDetailsPannel } from "./ProductDetailsPannel";
import ProductImagePannel from "./ProductImagePannel";

export interface Review {
  id: number;
  bookId: number;
  name: string;
  rating: number;
  text: string;
  createdAt: string;
  updatedAt: string;
  book?: {
    id: number;
    name: string;
  };
}

interface Category {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
  books?: Product[];
}

export interface Product {
  id: number;
  name: string;
  imageUrl?: string;
  imageUrlOnPage?: string;
  description: string;
  price: number;
  stock: number;
  hide: boolean;
  images: string[];
  pages?: number;
  categoryId?: number;
  category?: Category;
  cod?: number;
  awbCode?: string;
  isbn?: string;
  createdAt: string;
  updatedAt: string;
  reviews?: Array<Review>;
  orderItems?: Array<{
    id: number;
    quantity: number;
    price: number;
    order: {
      id: number;
      createdAt: string;
      status: string;
    };
  }>;
}

export function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [newReviewName, setNewReviewName] = React.useState("");
  const [newReviewRating, setNewReviewRating] = React.useState<string>("5");
  const [newReviewText, setNewReviewText] = React.useState("");
  const [isSubmittingReview, setIsSubmittingReview] = React.useState(false);

  // Fetch product details
  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => {
      const response = await fetchWithAuth(`/api/admin/products/${id}`);
      if (!response.ok) throw new Error("Eroare la încărcarea produsului");
      const data = await response.json();
      return data as Product;
    },
  });

  console.log("product", product);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-gray-50">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          Produsul nu a fost găsit
        </h1>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate("/products/")}
        >
          <ArrowLeft className="w-4 h-4" />
          Înapoi la produse
        </Button>
      </div>
    );
  }

  // Calculate sales statistics
  const totalSales =
    product.orderItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const totalRevenue =
    product.orderItems?.reduce(
      (sum, item) => sum + item.quantity * Number(item.price),
      0
    ) || 0;
  const averagePrice =
    totalSales > 0 ? Math.round((totalRevenue / totalSales) * 100) / 100 : 0;

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            <p className="text-sm text-gray-500">ID: #{product.id}</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate("/products/")}
        >
          <ArrowLeft className="w-4 h-4" />
          Înapoi la produse
        </Button>
      </div>

      <ProductImagePannel product={product} />

      {/* Product Details */}
      <ProductDetailsPannel product={product} />

      {/* Sales Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <ShoppingCart className="w-4 h-4" />
              Total Vânzări
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {totalSales} bucăți
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Venit Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(totalRevenue)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Preț Mediu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(averagePrice)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reviews Panel */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">Recenzii</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add Review Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nume
                </label>
                <Input
                  placeholder="Numele autorului recenziei"
                  value={newReviewName}
                  onChange={(e) => setNewReviewName(e.target.value)}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rating
                </label>
                <Select
                  value={newReviewRating}
                  onValueChange={(v) => setNewReviewRating(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alege rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1 md:col-span-1 flex items-end">
                <Button
                  onClick={async () => {
                    if (!id) return;
                    if (!newReviewName.trim() || !newReviewText.trim()) {
                      toast.error("Completează numele și textul recenziei");
                      return;
                    }
                    const rating = parseInt(newReviewRating, 10);
                    if (isNaN(rating) || rating < 1 || rating > 5) {
                      toast.error("Ratingul trebuie să fie între 1 și 5");
                      return;
                    }
                    try {
                      setIsSubmittingReview(true);
                      const response = await fetchWithAuth(
                        `/api/admin/reviews`,
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            bookId: Number(id),
                            name: newReviewName.trim(),
                            rating,
                            text: newReviewText.trim(),
                          }),
                        }
                      );
                      if (!response.ok) {
                        const err = await response.json().catch(() => ({}));
                        throw new Error(
                          err.message || "Eroare la adăugarea recenziei"
                        );
                      }
                      await queryClient.invalidateQueries({
                        queryKey: ["product", id],
                      });
                      setNewReviewName("");
                      setNewReviewRating("5");
                      setNewReviewText("");
                      toast.success("Recenzie adăugată cu succes");
                    } catch (e) {
                      toast.error(
                        e instanceof Error ? e.message : "Eroare necunoscută"
                      );
                    } finally {
                      setIsSubmittingReview(false);
                    }
                  }}
                  disabled={isSubmittingReview}
                  className="w-full md:w-auto"
                >
                  {isSubmittingReview ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Se salvează...
                    </span>
                  ) : (
                    "Adaugă recenzie"
                  )}
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Text recenzie
              </label>
              <textarea
                value={newReviewText}
                onChange={(e) => setNewReviewText(e.target.value)}
                placeholder="Scrie recenzia aici..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[100px]"
              />
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-2">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Nume</TableHead>
                  <TableHead className="font-semibold">Rating</TableHead>
                  <TableHead className="font-semibold">Text</TableHead>
                  <TableHead className="font-semibold">Creat la</TableHead>
                  <TableHead className="font-semibold">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!product.reviews?.length ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <div className="text-gray-500">Nu există recenzii</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  product.reviews.map((review) => (
                    <TableRow key={review.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        {review.name}
                      </TableCell>
                      <TableCell>{review.rating}</TableCell>
                      <TableCell className="max-w-[500px] whitespace-pre-wrap">
                        {review.text}
                      </TableCell>
                      <TableCell>{formatDate(review.createdAt)}</TableCell>
                      <TableCell>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="flex items-center gap-1"
                          onClick={async () => {
                            const confirmed = window.confirm(
                              "Sigur doriți să ștergeți această recenzie?"
                            );
                            if (!confirmed) return;
                            try {
                              const response = await fetchWithAuth(
                                `/api/admin/reviews/${review.id}`,
                                { method: "DELETE" }
                              );
                              if (!response.ok) {
                                const err = await response
                                  .json()
                                  .catch(() => ({}));
                                throw new Error(
                                  err.message || "Eroare la ștergerea recenziei"
                                );
                              }
                              await queryClient.invalidateQueries({
                                queryKey: ["product", id],
                              });
                              toast.success("Recenzie ștearsă cu succes");
                            } catch (e) {
                              toast.error(
                                e instanceof Error
                                  ? e.message
                                  : "Eroare necunoscută"
                              );
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                          Șterge
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Order History */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Istoric Comenzi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Comandă</TableHead>
                <TableHead className="font-semibold">Data</TableHead>
                <TableHead className="font-semibold">Cantitate</TableHead>
                <TableHead className="font-semibold">Preț</TableHead>
                <TableHead className="font-semibold">Total</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!product.orderItems?.length ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-gray-500">Nu există comenzi</div>
                  </TableCell>
                </TableRow>
              ) : (
                product.orderItems.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      #{item.order.id}
                    </TableCell>
                    <TableCell>{formatDate(item.order.createdAt)}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{formatPrice(item.price)}</TableCell>
                    <TableCell>
                      {formatPrice(item.quantity * Number(item.price))}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          item.order.status === "DELIVERED"
                            ? "bg-green-100 text-green-800 ring-1 ring-green-200"
                            : item.order.status === "PENDING"
                            ? "bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200"
                            : "bg-gray-100 text-gray-800 ring-1 ring-gray-200"
                        }
                      >
                        {item.order.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                        onClick={() => navigate(`/orders/${item.order.id}/`)}
                      >
                        <Eye className="w-4 h-4" />
                        Detalii
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
